import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Download, Copy, RefreshCw, QrCode as QrIcon } from 'lucide-react';
import Button from './Button';
import { activosService } from '../../services/activos';
import toast from 'react-hot-toast';

const QRDisplay = ({ activo, onClose, size = 256 }) => {
    const [qrData, setQrData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadQRData();
    }, [activo?.id]);

    const loadQRData = async () => {
        if (!activo?.id) return;

        try {
            setLoading(true);
            const data = await activosService.getQR(activo.id);
            setQrData(data);
        } catch (error) {
            console.error('Error loading QR:', error);
            toast.error('Error al cargar código QR');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!qrData) return;

        const svg = document.getElementById(`qr-code-${activo.id}`);
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        canvas.width = size;
        canvas.height = size;

        img.onload = () => {
            ctx.drawImage(img, 0, 0);
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `QR_${activo.code}.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    };

    const handleCopyURL = async () => {
        if (!qrData?.qr_url) return;

        try {
            await navigator.clipboard.writeText(qrData.qr_url);
            toast.success('URL copiada al portapapeles');
        } catch (error) {
            toast.error('Error al copiar URL');
        }
    };

    const handleRegenerate = async () => {
        if (!activo?.id) return;

        try {
            setLoading(true);
            await activosService.regenerateQR(activo.id);
            await loadQRData();
            toast.success('Código QR regenerado correctamente');
        } catch (error) {
            console.error('Error regenerating QR:', error);
            toast.error('Error al regenerar código QR');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando código QR...</p>
            </div>
        );
    }

    if (!qrData) {
        return (
            <div className="text-center py-8">
                <QrIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No se pudo cargar el código QR</p>
                <Button onClick={loadQRData} variant="outline" className="mt-4">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="text-center space-y-4">
            {/* QR Code Display */}
            <div className="bg-white p-4 rounded-lg border inline-block">
                <QRCode
                    id={`qr-code-${activo.id}`}
                    value={qrData.qr_url}
                    size={size}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
            </div>

            {/* Activo Info */}
            <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Código:</strong> {activo.code}</p>
                <p><strong>Nombre:</strong> {activo.name}</p>
                <p><strong>Ubicación:</strong> {activo.ubicado}</p>
            </div>

            {/* URL Display */}
            <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">URL del código QR:</p>
                <p className="text-sm font-mono text-gray-800 break-all">
                    {qrData.qr_url}
                </p>
            </div>

            {/* Actions */}
            <div className="flex justify-center space-x-3">
                <Button onClick={handleCopyURL} variant="outline" size="sm">
                    <Copy className="h-4 w-4 mr-1" />
                    Copiar URL
                </Button>
                
                <Button onClick={handleDownload} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    Descargar
                </Button>
                
                <Button onClick={handleRegenerate} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Regenerar
                </Button>
            </div>

            {onClose && (
                <div className="pt-4 border-t">
                    <Button onClick={onClose} variant="outline">
                        Cerrar
                    </Button>
                </div>
            )}
        </div>
    );
};

export default QRDisplay;